/**
 * DemoUsageMeter (Slice B)
 * ========================
 * Persistent, always-visible "X of N cell deep dives today" meter for DEMO
 * users. Goal-gradient / Zeigarnik: surfacing the open loop keeps the user
 * engaged toward the cap rather than being surprised by it. Never shown for
 * non-demo tiers or when the catalog is unlimited (PRO / admin, limit === -1).
 *
 * Reads live counters from demoLimitStore, which the data layer keeps fresh
 * from the X-Demo-Views-* success headers (and the usage-summary endpoint).
 */
import { useEffect, useState } from 'preact/hooks'
import { subscriptionTier } from '../../state/userState.js'
import {
  viewsUsed,
  viewsRemaining,
  UNLIMITED,
  isDemoCatalogUnlocked,
  getEffectiveViewsLimit,
  viewsLimitHydrated,
  viewedToday,
} from '../../state/demoLimitStore.js'
import { useResetCountdown } from '../../hooks/useResetCountdown.js'
import { generateCellSlug } from '../../utils/urlUtils.js'

export default function DemoUsageMeter() {
  // Subscribe to signals (read .value so Preact re-renders on change).
  const tier = subscriptionTier.value
  const used = viewsUsed.value
  const remaining = viewsRemaining.value
  const hydrated = viewsLimitHydrated.value

  // Collapsible list of the cells unlocked (deep-dived) today.
  const [open, setOpen] = useState(false)

  const isDemo = isDemoCatalogUnlocked()

  // Hooks must run unconditionally (before the early return below) — tier can
  // flip from non-demo to DEMO on the same mounted instance (e.g. login),
  // which would otherwise change the hook call order across renders. `enabled`
  // keeps the 1s interval from ticking forever for every non-demo user.
  const countdown = useResetCountdown(isDemo)

  // Once the countdown rolls over, re-pull the authoritative usage snapshot —
  // the backend's Redis quota key has rolled to the new UTC day by then.
  useEffect(() => {
    if (!isDemo || !countdown.expired) return
    import('../../services/demoLimitService.js')
      .then(({ refreshDemoUsageSummary }) => refreshDemoUsageSummary(true))
      .catch(() => {
        /* non-fatal — meter will re-sync on the next header/summary update */
      })
  }, [isDemo, countdown.expired])

  // Ensure the authoritative usage snapshot is fetched whenever the meter is shown
  // to a demo user that has NOT hydrated its real cap yet — e.g. right after a page
  // reload, before any cell has been opened. The AppShell boot fetch keys off
  // subscriptionTier via effect deps, but AppShell isn't render-subscribed to that
  // signal, so its effect can miss the free→DEMO flip and never (re)fire. This meter
  // IS subscribed (it reads isDemo + hydrated in render), so triggering here is
  // reliable. Fires once per unhydrated state; `hydrated` flips true after the fetch
  // so it can't loop. Without this the meter sat on the placeholder denominator +
  // empty bar until the first cell-open header arrived.
  useEffect(() => {
    if (!isDemo || hydrated) return
    import('../../services/demoLimitService.js')
      .then(({ refreshDemoUsageSummary }) => refreshDemoUsageSummary(true))
      .catch(() => {
        /* non-fatal — meter re-syncs on the next header/summary update */
      })
  }, [isDemo, hydrated])

  // The meter is UNCONDITIONALLY present for a DEMO user — on every page, from
  // "0 of N", regardless of the counter value or load state. It hides ONLY for
  // non-DEMO tiers. A missing/unlimited/zero cap during a boot or post-reset
  // sync gap no longer blanks it: getEffectiveViewsLimit() supplies a safe
  // display cap so "X of N" is never broken (it reads viewsLimit.value, which
  // keeps the raw signal subscribed for re-renders).
  if (!isDemo) {
    return null
  }

  const effectiveLimit = getEffectiveViewsLimit()
  const clampedUsed = Math.min(Math.max(0, used ?? 0), effectiveLimit)
  // Until the authoritative per-account cap has hydrated, show a placeholder
  // denominator ("X of …") rather than the /config/public daily fallback — a
  // first-48h account's real cap is higher, and showing the fallback first would
  // produce the visible "0 of 5" → "1 of 10" tier jump. The meter CHROME stays
  // always-visible (BA core requirement); only the number is deferred (<1s, the
  // usage-summary fires at boot). After hydration → the real "X of N".
  const denomLabel = hydrated ? effectiveLimit : '…'
  const pct = hydrated ? Math.min(100, Math.round((clampedUsed / effectiveLimit) * 100)) : 0
  // Warn colour when 1 or fewer deep-dives remain (or already at the cap).
  // Suppressed pre-hydration — we don't yet know the true remaining.
  const near = hydrated && remaining !== UNLIMITED && remaining <= 1

  // Cells unlocked (deep-dived) today — the expandable list.
  const unlocked = viewedToday.value || []
  const hasUnlocked = unlocked.length > 0

  return (
    <div
      className={`demo-usage-meter${near ? ' demo-usage-meter--warn' : ''}`}
      role="status"
      aria-live="polite"
      aria-label={`${clampedUsed} of ${denomLabel} cell deep dives used today. Browsing all cells is free.`}
    >
      <div className="demo-usage-meter__row">
        <span className="demo-usage-meter__label">
          <span className="demo-usage-meter__count">
            {clampedUsed} of {denomLabel}
          </span>{' '}
          deep dives today
          {hasUnlocked && (
            <button
              type="button"
              className={`demo-usage-meter__toggle${open ? ' is-open' : ''}`}
              onClick={() => setOpen((o) => !o)}
              aria-expanded={open}
              title={open ? 'Hide unlocked cells' : 'Show the cells you unlocked today'}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          )}
        </span>
        <span className="demo-usage-meter__reset">resets in {countdown.formatted}</span>
      </div>
      <div className="demo-usage-meter__track">
        <div className="demo-usage-meter__fill" style={{ width: `${pct}%` }} />
      </div>
      {open && hasUnlocked && (
        <ul className="demo-usage-meter__unlocked">
          {unlocked.map((c) => {
            const slug = generateCellSlug(c)
            const label = c.name
              ? `${c.manufacturer ? `${c.manufacturer} — ` : ''}${c.name}`
              : c.product_id
            return (
              <li key={c.product_id} className="demo-usage-meter__unlocked-item">
                {slug ? (
                  <a href={`/explorer?cell=${slug}&section=factsheet`}>{label}</a>
                ) : (
                  <span>{label}</span>
                )}
              </li>
            )
          })}
        </ul>
      )}
      <span className="demo-usage-meter__reassure">Browsing all cells is free.</span>
    </div>
  )
}
